import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/GoogleCloud.css";
import googleCloudLogo from "../../assets/images/partners/Google-Cloud-Logo.png";

function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const whyCards = [
  { icon: "🌐", title: "Global Network Infrastructure", body: "Google's private fiber network spans 33 regions and 100+ zones globally — delivering the lowest latency and highest throughput of any public cloud provider for compute, storage, and AI workloads.", color: "linear-gradient(90deg,#4285f4,#1a73e8)" },
  { icon: "🤖", title: "AI-First Platform", body: "Born from Google's AI research, Google Cloud offers the most mature AI/ML platform in the industry — Vertex AI, Gemini API, AutoML, and TPU hardware not available anywhere else.", color: "linear-gradient(90deg,#ea4335,#c5221f)" },
  { icon: "💰", title: "Sustained Use Discounts", body: "Google Cloud automatically applies sustained use discounts for long-running workloads without requiring upfront commitments — delivering savings of 20–57% compared to on-demand pricing.", color: "linear-gradient(90deg,#fbbc04,#e8a000)" },
  { icon: "🔒", title: "Google-Grade Security", body: "The same zero-trust BeyondCorp security model that protects Google's own infrastructure — now available for your organization through Chronicle, Security Command Center, and Binary Authorization.", color: "linear-gradient(90deg,#34a853,#0f9d58)" },
];

const aiProducts = [
  { icon: "🧠", name: "Vertex AI", desc: "Unified ML platform for training, deploying, and managing models at scale. Includes AutoML, custom training, Vertex AI Workbench, and the Gemini API for generative AI applications." },
  { icon: "🔤", name: "Gemini on Google Cloud", desc: "Google's most capable multimodal AI model available via API — supporting text, code, image, audio, and video understanding for enterprise applications." },
  { icon: "📊", name: "BigQuery ML", desc: "Train and deploy machine learning models directly within BigQuery using SQL — no data movement required, democratizing ML across data teams." },
  { icon: "🎯", name: "Document AI & Contact Center AI", desc: "Pre-built AI solutions for document processing, customer service automation, and conversational AI — deployed in hours, not months." },
  { icon: "🚀", name: "Duet AI for Developers", desc: "AI-powered development assistance integrated into Cloud Shell, Cloud Code, and Google's developer tools — accelerating code generation, review, and debugging." },
];

const infraTiles = [
  { icon: "⚡", name: "Compute Engine", area: "IaaS Compute", colorClass: "gc-infra-tile__top-bg-blue", desc: "High-performance VMs with custom machine types, spot VMs, sole-tenant nodes, and confidential computing — running on Google's custom Titanium hardware.", pills: ["C3/N4 VMs", "Spot VMs", "Sole-Tenant", "Confidential VMs"], wide: true },
  { icon: "📦", name: "Google Kubernetes Engine", area: "Container Orchestration", colorClass: "gc-infra-tile__top-bg-green", desc: "Autopilot and standard GKE clusters — the most secure, production-ready managed Kubernetes with automatic upgrades, multi-cluster mesh, and built-in autoscaling.", pills: ["Autopilot", "Standard Mode", "GKE Enterprise"], wide: false },
  { icon: "🗄️", name: "Cloud Storage & Spanner", area: "Storage & Databases", colorClass: "gc-infra-tile__top-bg-red", desc: "Cloud Storage for object storage, AlloyDB for PostgreSQL-compatible transactional workloads, and Cloud Spanner for globally consistent ACID databases at planetary scale.", pills: ["Cloud Storage", "AlloyDB", "Cloud Spanner", "BigTable"], wide: false },
  { icon: "🌐", name: "Cloud Load Balancing & CDN", area: "Global Networking", colorClass: "gc-infra-tile__top-bg-indigo", desc: "Global anycast load balancing with sub-second failover, Cloud CDN on Google's edge network, and Cloud Armor DDoS protection backed by Google's global threat intelligence.", pills: ["Global LB", "Cloud CDN", "Cloud Armor", "Network Intelligence"], wide: false },
  { icon: "🔁", name: "Cloud Run & App Engine", area: "Serverless Platform", colorClass: "gc-infra-tile__top-bg-yellow", desc: "Fully managed serverless platforms that scale to zero — Cloud Run for containerized workloads and App Engine for web applications — with automatic scaling and no infrastructure management.", pills: ["Cloud Run", "App Engine", "Cloud Functions", "Workflows"], wide: false },
];

const k8sFeatures = [
  { name: "Autopilot Mode", desc: "Google manages node provisioning, scaling, and security patching — you only pay for running pods." },
  { name: "GKE Multi-Cluster", desc: "Federated multi-cluster management with Anthos Service Mesh for consistent policy enforcement." },
  { name: "Binary Authorization", desc: "Policy-based deployment control ensuring only verified container images run in your clusters." },
  { name: "Workload Identity", desc: "Secure pod-to-Google-Cloud-service authentication without storing service account keys." },
  { name: "Config Connector", desc: "Manage Google Cloud resources using Kubernetes manifests — GitOps-friendly IaC workflow." },
  { name: "Cloud Deploy", desc: "Fully managed continuous delivery to GKE with built-in approval gates and rollback capabilities." },
];

const dataSuite = [
  { icon: "🔬", name: "BigQuery", desc: "Serverless, petabyte-scale data warehouse with built-in ML, BI Engine, and Omni for multi-cloud analysis. Processes 1TB of analytical queries for ~$5." },
  { icon: "📡", name: "Pub/Sub & Dataflow", desc: "Real-time messaging with Pub/Sub delivering 500M messages/sec and managed Apache Beam pipelines through Dataflow for streaming ETL." },
  { icon: "👁️", name: "Looker & Looker Studio", desc: "Enterprise BI platform with semantic layer modeling for consistent business metrics — embedded analytics and self-service dashboards at scale." },
  { icon: "🏗️", name: "Dataplex & Data Catalog", desc: "Unified data governance, data mesh architecture, and automatic metadata discovery across BigQuery, Cloud Storage, and external sources." },
];

const appCards = [
  { icon: "🚀", name: "Cloud Run", desc: "Deploy any containerized application serverlessly — auto-scales to millions of requests per second, scales to zero when idle, charges only for actual execution.", tags: ["Serverless", "Containers", "Auto-Scale"], colorClass: "gc-app-card--blue" },
  { icon: "🔌", name: "Apigee API Gateway", desc: "Enterprise API management platform — design, secure, publish, and monetize APIs with built-in analytics, developer portals, and OAuth integration.", tags: ["API Gateway", "Developer Portal", "Analytics"], colorClass: "gc-app-card--green" },
  { icon: "⚡", name: "Cloud Functions", desc: "Event-driven serverless functions for microservices architectures — triggered by HTTP, Pub/Sub events, Firebase, or Cloud Storage changes.", tags: ["Functions", "Event-Driven", "Microservices"], colorClass: "gc-app-card--red" },
  { icon: "🔄", name: "Cloud Workflows", desc: "Orchestrate and automate Google Cloud and external HTTP API-based services into resilient, serverless workflows with built-in error handling.", tags: ["Orchestration", "Automation", "Integration"], colorClass: "gc-app-card--yellow" },
  { icon: "💡", name: "Firebase & App Engine", desc: "Full-featured backend as a service for mobile and web applications — real-time database, authentication, hosting, and ML Kit in a developer-friendly platform.", tags: ["Mobile", "Web", "Real-Time"], colorClass: "gc-app-card--indigo" },
  { icon: "🌐", name: "Anthos & GKE Enterprise", desc: "Manage Kubernetes applications consistently across on-premises, Google Cloud, AWS, and Azure — unified policy, service mesh, and observability everywhere.", tags: ["Multi-Cloud", "Hybrid", "Policy"], colorClass: "gc-app-card--teal" },
];

const securityChecks = [
  { icon: "🛡️", title: "Chronicle SIEM & SOAR", desc: "Google-scale security analytics built on the same infrastructure as Google Search — ingesting petabytes of telemetry to detect threats in seconds that legacy SIEMs miss." },
  { icon: "🔍", title: "Security Command Center", desc: "Unified security and risk dashboard across all Google Cloud services — with AI-powered threat detection, misconfiguration findings, and vulnerability management." },
  { icon: "🔑", title: "Cloud Identity & BeyondCorp", desc: "Google's zero-trust enterprise security model extended to any application — enabling secure access to resources from any device without VPN." },
  { icon: "🔐", title: "Confidential Computing", desc: "Encrypt data in use with hardware-based Trusted Execution Environments — ensuring workloads can't be inspected even by Google Cloud administrators." },
];

const industries = [
  { icon: "🏥", name: "Healthcare & Life Sciences", desc: "HIPAA-eligible Google Cloud with Cloud Healthcare API for FHIR/HL7, BigQuery for genomics, and Vertex AI for clinical decision support." },
  { icon: "🏦", name: "Financial Services", desc: "PCI-DSS, SOC2 Type II cloud infrastructure with Financial Services Cloud for trading, risk analytics, and real-time fraud detection at scale." },
  { icon: "🛒", name: "Retail & E-Commerce", desc: "Recommendations AI, inventory optimization, and Google Search quality applied to your product catalog — plus global CDN for peak traffic." },
  { icon: "🚀", name: "Technology & SaaS", desc: "The platform that scales with growth — from startup to hyperscale — with GKE, Cloud Run, AlloyDB, and developer-friendly APIs your engineers love." },
  { icon: "🎓", name: "Education", desc: "Google Workspace for Education combined with BigQuery for learning analytics and Vertex AI for personalized learning experiences." },
  { icon: "🏛️", name: "Government", desc: "FedRAMP Authorized and IL4/IL5 Google Cloud with dedicated Government Cloud regions for classified and sensitive government workloads." },
  { icon: "🏭", name: "Manufacturing", desc: "Edge computing with Distributed Cloud, IoT streams to Pub/Sub, and AI-powered predictive maintenance using Vertex AI and time-series data." },
  { icon: "🎬", name: "Media & Entertainment", desc: "Transcode API for broadcast-quality video processing, global CDN for live streaming, and AI-powered content moderation at YouTube scale." },
];

const migrationSteps = [
  { num: "1", icon: "🔍", title: "Assess & Discover", desc: "Inventory workloads using Migrate for Anthos and StratoZone for automated discovery.", colorClass: "gc-migration__num--1" },
  { num: "2", icon: "📐", title: "Plan & Architect", desc: "Design target GCP architecture with right-sized resources and landing zone best practices.", colorClass: "gc-migration__num--2" },
  { num: "3", icon: "🚀", title: "Pilot & Validate", desc: "Migrate a low-risk workload first — validate performance, cost, and operational procedures.", colorClass: "gc-migration__num--3" },
  { num: "4", icon: "🔄", title: "Migrate & Modernize", desc: "Execute phased migration using Migrate for Compute Engine and Database Migration Service.", colorClass: "gc-migration__num--4" },
  { num: "5", icon: "⚡", title: "Optimize & Scale", desc: "Apply committed use discounts, rightsizing recommendations, and modernize with serverless.", colorClass: "gc-migration__num--5" },
];

const certs = [
  { icon: "🏆", name: "Google Cloud Partner", desc: "Authorized Google Cloud partner with sales and technical capabilities across GCP services" },
  { icon: "☁️", name: "Cloud Engineer Certified", desc: "Associate Cloud Engineer and Professional Cloud Architect certifications" },
  { icon: "🤖", name: "ML/AI Specialization", desc: "Professional Machine Learning Engineer certified team members for Vertex AI deployments" },
  { icon: "🔒", name: "Security Specialization", desc: "Professional Cloud Security Engineer for Chronicle, SCC, and BeyondCorp implementation" },
];

const faqData = [
  { q: "How does Google Cloud compare to AWS and Microsoft Azure for enterprise workloads?", a: "Google Cloud excels in three areas where it holds clear technical advantages: AI/ML infrastructure (Vertex AI, Gemini API, TPU hardware), data analytics (BigQuery's serverless architecture at petabyte scale), and global network performance (Google's private fiber backbone). For Kubernetes, Google invented it — GKE Autopilot remains the most mature managed Kubernetes service. For cost, Google's sustained use discounts and custom machine types often deliver lower total cost of ownership for compute-intensive workloads. ALG Data Guard helps organizations evaluate cloud platforms based on their specific workload characteristics, existing investments, and long-term roadmap." },
  { q: "What is Vertex AI and how can organizations use it for enterprise AI?", a: "Vertex AI is Google Cloud's unified machine learning platform that consolidates the full ML lifecycle — from data preparation and model training through deployment, monitoring, and MLOps. Vertex AI includes: AutoML for no-code model training, custom training jobs with support for TensorFlow, PyTorch, and scikit-learn, the Gemini API for accessing Google's most advanced language and multimodal models, Vertex AI Workbench for managed Jupyter notebooks, and Model Registry for model versioning and governance. Organizations use Vertex AI for document classification, predictive analytics, recommendation engines, conversational AI, and image analysis. ALG Data Guard designs and implements Vertex AI pipelines tailored to your business use case." },
  { q: "What is Google Cloud's approach to hybrid and multi-cloud environments?", a: "Google Cloud's hybrid and multi-cloud strategy centers on Anthos — a managed application platform that extends Kubernetes consistently across on-premises infrastructure, Google Cloud, AWS, and Azure. Anthos provides consistent policy enforcement through Anthos Config Management, service mesh capabilities through Anthos Service Mesh (built on Istio), and unified observability through Cloud Monitoring and Cloud Logging regardless of where workloads run. Google Distributed Cloud extends this to air-gapped edge locations and sovereign cloud requirements. For organizations not ready to move fully to the cloud, this approach enables a genuine hybrid operating model rather than forcing all-or-nothing cloud migration." },
  { q: "How does ALG Data Guard help organizations migrate to Google Cloud?", a: "ALG Data Guard's Google Cloud migration practice covers the complete journey: infrastructure assessment using Google's StratoZone and Migrate for Anthos tooling to inventory existing workloads and estimate cloud cost, architecture design following Google Cloud's Well-Architected Framework and landing zone best practices, phased migration execution using Migrate for Compute Engine and Database Migration Service, modernization guidance for containerizing workloads on GKE or going serverless with Cloud Run, and post-migration optimization applying committed use discounts, rightsizing recommendations, and FinOps practices to manage ongoing cloud spend." },
  { q: "What compliance certifications does Google Cloud hold?", a: "Google Cloud is certified or compliant with over 150 regulatory standards globally, including: FedRAMP Moderate and High (with Government Cloud regions for IL4 and IL5), HIPAA Business Associate Agreements for healthcare workloads, PCI DSS Level 1 for payment processing, SOC 1/2/3 Type II, ISO 27001/27017/27018, GDPR compliance with data processing addendums, and country-specific certifications for the EU, Japan, Singapore, and other jurisdictions. Google Cloud's Assured Workloads feature enables organizations to apply compliance controls automatically to projects — restricting data residency, operator access, and supported services to meet regulatory requirements." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function GoogleCloud() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);
  const c1 = useCounter(200, 2200, metricsInView);
  const c2 = useCounter(99, 2000, metricsInView);
  const c3 = useCounter(150, 2000, metricsInView);
  const c4 = useCounter(40, 2000, metricsInView);

  return (
    <div className="gc-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={googleCloudLogo} alt="Google Cloud" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Cloud & AI Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Google Cloud</h1>
            <p className="pd-identity-desc">
              Google Cloud is the cloud platform born from the same infrastructure that powers Google Search, YouTube, and Gmail — delivering unmatched AI capabilities, the most advanced data analytics platform (BigQuery), and a global private fiber network spanning 33 regions. ALG Data Guard is an authorized Google Cloud Partner, helping organizations design, migrate, and modernize on GCP.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="gc-hero">
        <div className="gc-hero__blob gc-hero__blob--1" aria-hidden="true" />
        <div className="gc-hero__blob gc-hero__blob--2" aria-hidden="true" />
        <div className="gc-hero__blob gc-hero__blob--3" aria-hidden="true" />
        <div className="gc-hero__grid-lines" aria-hidden="true" />
        <div className="gc-hero__inner">
          <div>
            <div className="gc-chip gc-chip--dark"><span className="gc-chip-dot" /> Google Cloud Partner</div>
            <h2 className="gc-hero__title">
              <span className="gc-hero__title-blue">Think</span>{" "}
              <span className="gc-hero__title-red">Build</span>{" "}
              <span className="gc-hero__title-yellow">Scale</span>{" "}
              <span className="gc-hero__title-green">Secure</span><br/>
              on Google Cloud.
            </h2>
            <p className="gc-hero__body">
              Google Cloud brings AI, data, and infrastructure together in one seamlessly integrated platform — giving your organization the same technological advantages Google uses internally, delivered at enterprise scale with ALG Data Guard's expert implementation and managed services.
            </p>
            <div className="gc-hero__actions">
              <button className="gc-btn gc-btn--blue" onClick={() => navigate("/contact")}>
                Start Your Cloud Journey <ArrowIcon />
              </button>
              <button className="gc-btn gc-btn--white-outline" onClick={() => navigate("/contact")}>
                Talk to a GCP Expert
              </button>
            </div>
            <div className="gc-hero__pills">
              {["Compute Engine", "GKE", "Vertex AI", "BigQuery", "Cloud Run", "Apigee", "Chronicle", "Looker", "Spanner", "Pub/Sub"].map(p => (
                <span key={p} className="gc-hero__pill">{p}</span>
              ))}
            </div>
          </div>

          <div className="gc-service-cluster">
            <div className="gc-float-card gc-float-card--1">
              <div className="gc-float-card__label">Vertex AI — Model Training</div>
              <div className="gc-float-card__rows">
                {[
                  { dot:"#4285f4", label:"Gemini 1.5 Pro Training", val:"Running" },
                  { dot:"#34a853", label:"AutoML Image Classification", val:"98.2% Acc." },
                  { dot:"#fbbc04", label:"Custom PyTorch Job — A100", val:"Epoch 47/100" },
                ].map(r => (
                  <div key={r.label} className="gc-float-card__row">
                    <span className="gc-float-card__dot" style={{ background:r.dot }} />
                    <span className="gc-float-card__row-label">{r.label}</span>
                    <span className="gc-float-card__row-val">{r.val}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="gc-float-card gc-float-card--2">
              <div className="gc-float-card__label">BigQuery — Data Processed</div>
              <div className="gc-float-card__stat gc-stat--blue">4.2 PB</div>
              <div className="gc-float-card__sub">This month across 24 projects</div>
            </div>
            <div className="gc-float-card gc-float-card--3">
              <div className="gc-float-card__label">GKE Autopilot</div>
              <div className="gc-float-card__stat gc-stat--green">847</div>
              <div className="gc-float-card__sub">Pods running across 3 clusters</div>
            </div>
            <div className="gc-float-card gc-float-card--4">
              <div className="gc-float-card__label">Cloud Carbon Footprint</div>
              <div className="gc-float-card__row" style={{ marginBottom:"0.4rem" }}>
                <span className="gc-float-card__dot" style={{ background:"#34a853" }} />
                <span className="gc-float-card__row-label">Carbon offset operations</span>
                <span className="gc-float-card__row-val gc-stat--green">100% ♻</span>
              </div>
              <div className="gc-float-card__row">
                <span className="gc-float-card__dot" style={{ background:"#4285f4" }} />
                <span className="gc-float-card__row-label">Matched renewable energy</span>
                <span className="gc-float-card__row-val" style={{ color:"#7ab4fc" }}>24/7</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why Google Cloud ── */}
      <section className="gc-why">
        <div className="gc-shell">
          <div className="gc-why__head">
            <div className="gc-rainbow-bar" />
            <h2 className="gc-title">Why Industry Leaders Choose Google Cloud</h2>
            <p className="gc-sub" style={{ margin:"0 auto", maxWidth:"640px" }}>
              Google Cloud isn't just another hyperscaler. It's the platform built by the company that pioneered modern infrastructure, machine learning, and open-source technology.
            </p>
          </div>
          <div className="gc-why__grid">
            {whyCards.map(c => (
              <div key={c.title} className="gc-why-card">
                <div className="gc-why-card__color-strip" style={{ background:c.color }} />
                <span className="gc-why-card__icon">{c.icon}</span>
                <h3 className="gc-why-card__title">{c.title}</h3>
                <p className="gc-why-card__body">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AI & ML ── */}
      <section className="gc-ai">
        <div className="gc-ai__blob" aria-hidden="true" />
        <div className="gc-shell">
          <div className="gc-ai__inner">
            <div>
              <div className="gc-chip gc-chip--dark"><span className="gc-chip-dot" /> AI & Machine Learning</div>
              <h2 className="gc-ai__title">The Most Advanced AI Platform in the Cloud</h2>
              <p className="gc-ai__body">
                Google Cloud gives enterprises direct access to the same AI research that produced Gemini, AlphaFold, and BERT — through Vertex AI, the world's most complete managed ML platform. From training custom models to deploying pre-built AI APIs, Google Cloud compresses the path from AI experimentation to production.
              </p>
              <div className="gc-ai__products">
                {aiProducts.map(p => (
                  <div key={p.name} className="gc-ai-product">
                    <span className="gc-ai-product__icon">{p.icon}</span>
                    <div>
                      <div className="gc-ai-product__name">{p.name}</div>
                      <p className="gc-ai-product__desc">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="gc-btn gc-btn--blue" onClick={() => navigate("/contact")}>
                  Explore AI/ML Solutions <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1677442135703-1787eea5ce01?auto=format&fit=crop&w=900&q=80"
              alt="AI infrastructure and machine learning"
              className="gc-ai__img"
            />
          </div>
        </div>
      </section>

      {/* ── Infrastructure ── */}
      <section className="gc-infra">
        <div className="gc-shell">
          <div className="gc-infra__head">
            <div className="gc-chip gc-chip--blue"><span className="gc-chip-dot" /> Cloud Infrastructure</div>
            <h2 className="gc-title">Enterprise-Grade Infrastructure Built for Scale</h2>
            <p className="gc-sub" style={{ margin:"0 auto", maxWidth:"640px" }}>
              Google Cloud's infrastructure stack spans compute, containers, storage, databases, and networking — all optimized for performance on Google's global private network.
            </p>
          </div>
          <div className="gc-infra-bento">
            {infraTiles.map(tile => (
              <div key={tile.name} className={`gc-infra-tile${tile.wide ? " gc-infra-tile--wide" : ""}`}>
                <div className={`gc-infra-tile__top ${tile.colorClass}`}>
                  <span className="gc-infra-tile__icon">{tile.icon}</span>
                  <div className="gc-infra-tile__service-name">{tile.name}</div>
                </div>
                <div className="gc-infra-tile__body">
                  <div className="gc-infra-tile__area">{tile.area}</div>
                  <p className="gc-infra-tile__desc">{tile.desc}</p>
                  <div className="gc-infra-tile__pills">{tile.pills.map(p => <span key={p} className="gc-infra-tile__pill">{p}</span>)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Kubernetes ── */}
      <section className="gc-k8s">
        <div className="gc-shell">
          <div className="gc-k8s__inner">
            <img
              src="https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=900&q=80"
              alt="Kubernetes and containers"
              className="gc-k8s__img"
            />
            <div>
              <div className="gc-chip gc-chip--green"><span className="gc-chip-dot" /> Kubernetes & Containers</div>
              <h2 className="gc-k8s__title">GKE — The Best Kubernetes, From Its Inventors</h2>
              <p className="gc-k8s__body">
                Google invented Kubernetes and donated it to the CNCF — so no one knows how to run it better. GKE Autopilot eliminates node management entirely: Google provisions, scales, secures, and patches nodes automatically while you focus on applications. ALG Data Guard designs and operates production GKE environments with enterprise security policies, GitOps workflows, and observability.
              </p>
              <div className="gc-k8s__features">
                {k8sFeatures.map(f => (
                  <div key={f.name} className="gc-k8s-feature">
                    <div className="gc-k8s-feature__name">{f.name}</div>
                    <p className="gc-k8s-feature__desc">{f.desc}</p>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="gc-btn gc-btn--blue" style={{ background:"var(--gc-green)" }} onClick={() => navigate("/contact")}>
                  Design My GKE Environment <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Data Analytics ── */}
      <section className="gc-data">
        <div className="gc-shell">
          <div className="gc-data__inner">
            <div>
              <div className="gc-chip gc-chip--yellow"><span className="gc-chip-dot" /> Data & Analytics</div>
              <h2 className="gc-title">From Raw Data to Real Insight, at Any Scale</h2>
              <p className="gc-sub" style={{ marginBottom:"1rem" }}>
                Google Cloud's data analytics platform is the most comprehensive in the cloud — built around BigQuery's serverless, petabyte-scale SQL engine that has processed more analytical queries than any other platform in history.
              </p>
              <p className="gc-sub" style={{ marginBottom:"2rem" }}>
                ALG Data Guard designs enterprise data platforms on Google Cloud — from real-time streaming pipelines to governed data mesh architectures that make data available across your organization.
              </p>
              <div className="gc-data__suite">
                {dataSuite.map(s => (
                  <div key={s.name} className="gc-data-suite-item">
                    <span className="gc-data-suite-item__icon">{s.icon}</span>
                    <div>
                      <div className="gc-data-suite-item__name">{s.name}</div>
                      <p className="gc-data-suite-item__desc">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=80"
              alt="Data analytics dashboard"
              className="gc-data__img"
            />
          </div>
        </div>
      </section>

      {/* ── App Platform ── */}
      <section className="gc-app">
        <div className="gc-shell">
          <div className="gc-app__head">
            <div className="gc-chip gc-chip--red"><span className="gc-chip-dot" /> Application Platform</div>
            <h2 className="gc-title">Build and Deploy Applications, Your Way</h2>
            <p className="gc-sub" style={{ margin:"0 auto", maxWidth:"640px" }}>
              From fully managed serverless to raw container infrastructure, Google Cloud's application platform gives developers the right level of abstraction for every workload.
            </p>
          </div>
          <div className="gc-app__grid">
            {appCards.map(card => (
              <div key={card.name} className={`gc-app-card ${card.colorClass}`}>
                <span className="gc-app-card__icon">{card.icon}</span>
                <h3 className="gc-app-card__name">{card.name}</h3>
                <p className="gc-app-card__desc">{card.desc}</p>
                <div className="gc-app-card__tags">{card.tags.map(t => <span key={t} className="gc-app-card__tag">{t}</span>)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Security ── */}
      <section className="gc-security">
        <div className="gc-shell">
          <div className="gc-security__inner">
            <img
              src="https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=900&q=80"
              alt="Google Cloud security operations"
              className="gc-security__img"
            />
            <div>
              <div className="gc-chip gc-chip--green"><span className="gc-chip-dot" /> Cloud Security</div>
              <h2 className="gc-title">Security at the Scale of Google</h2>
              <p className="gc-sub" style={{ marginBottom:"0.5rem" }}>
                Google has been operating at hyperscale under continuous nation-state threat for decades. That security intelligence — from BeyondCorp zero trust to Chronicle's petabyte-scale SIEM — is now available to your organization through Google Cloud.
              </p>
              <div className="gc-security__checklist">
                {securityChecks.map(s => (
                  <div key={s.title} className="gc-security-check">
                    <div className="gc-security-check__icon">{s.icon}</div>
                    <div>
                      <div className="gc-security-check__title">{s.title}</div>
                      <p className="gc-security-check__desc">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="gc-btn gc-btn--outline" onClick={() => navigate("/contact")}>
                  Explore Cloud Security <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="gc-industries">
        <div className="gc-shell">
          <div className="gc-industries__head">
            <div className="gc-chip gc-chip--blue"><span className="gc-chip-dot" /> Industry Solutions</div>
            <h2 className="gc-title">Google Cloud Across Every Industry</h2>
            <p className="gc-sub" style={{ margin:"0 auto", maxWidth:"640px" }}>
              Google Cloud's industry-specific solutions, compliance certifications, and partner ecosystem support every regulated and mission-critical environment.
            </p>
          </div>
          <div className="gc-industries__grid">
            {industries.map(ind => (
              <div key={ind.name} className="gc-industry-card">
                <span className="gc-industry-card__icon">{ind.icon}</span>
                <h3 className="gc-industry-card__name">{ind.name}</h3>
                <p className="gc-industry-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Migration Journey ── */}
      <section className="gc-migration">
        <div className="gc-shell">
          <div className="gc-migration__head">
            <div className="gc-chip gc-chip--dark"><span className="gc-chip-dot" /> Migration Journey</div>
            <h2 className="gc-title">Your Structured Path to Google Cloud</h2>
            <p className="gc-sub">ALG Data Guard's proven migration methodology reduces risk and accelerates time-to-value on Google Cloud.</p>
          </div>
          <div className="gc-migration__steps">
            {migrationSteps.map(step => (
              <div key={step.num} className="gc-migration__step">
                <div className={`gc-migration__num ${step.colorClass}`}>{step.icon}</div>
                <h3 className="gc-migration__step-title">{step.title}</h3>
                <p className="gc-migration__step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="gc-metrics" ref={metricsRef}>
        <div className="gc-shell">
          <div className="gc-metrics__inner">
            <div className="gc-metric"><div className="gc-metric__val gc-metric__val--blue">{c1}+</div><div className="gc-metric__label">GCP Projects Deployed</div></div>
            <div className="gc-metric"><div className="gc-metric__val gc-metric__val--red">{c2}.9%</div><div className="gc-metric__label">Cloud Uptime SLA</div></div>
            <div className="gc-metric"><div className="gc-metric__val gc-metric__val--green">{c3}+</div><div className="gc-metric__label">GCP Services Available</div></div>
            <div className="gc-metric"><div className="gc-metric__val gc-metric__val--yellow">{c4}+</div><div className="gc-metric__label">Countries Served</div></div>
          </div>
        </div>
      </section>

      {/* ── Certifications ── */}
      <section className="gc-certs">
        <div className="gc-shell">
          <div className="gc-certs__head">
            <div className="gc-rainbow-bar" style={{ margin:"0 auto 1rem" }} />
            <h2 className="gc-title">Certified Google Cloud Partner Expertise</h2>
            <p className="gc-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>
              ALG Data Guard's team holds Google Cloud professional certifications validated across the full GCP portfolio.
            </p>
          </div>
          <div className="gc-certs__grid">
            {certs.map(c => (
              <div key={c.name} className="gc-cert-card">
                <span className="gc-cert-card__icon">{c.icon}</span>
                <div className="gc-cert-card__name">{c.name}</div>
                <p className="gc-cert-card__desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="gc-faq">
        <div className="gc-shell">
          <div className="gc-faq__head">
            <div className="gc-chip gc-chip--blue" style={{ margin:"0 auto 1rem" }}><span className="gc-chip-dot" /> FAQs</div>
            <h2 className="gc-title">Google Cloud Questions, Answered</h2>
            <p className="gc-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>Expert answers about Google Cloud capabilities and ALG Data Guard's implementation services.</p>
          </div>
          <div className="gc-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`gc-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="gc-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="gc-faq-item__q">{faq.q}</span>
                  <span className="gc-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="gc-faq-item__answer"><div className="gc-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="gc-cta">
        <div className="gc-cta__blobs" aria-hidden="true" />
        <div className="gc-cta__inner">
          <div className="gc-cta__rainbow" />
          <h2 className="gc-cta__title">Unlock the Full Power of Google Cloud</h2>
          <p className="gc-cta__sub">
            From AI and analytics to Kubernetes and cloud security — ALG Data Guard's certified Google Cloud engineers design the right architecture and manage it so you can focus on what matters: your business.
          </p>
          <div className="gc-cta__actions">
            <button className="gc-btn gc-btn--blue" onClick={() => navigate("/contact")}>
              Start Your GCP Journey <ArrowIcon />
            </button>
            <button className="gc-btn gc-btn--white-outline" onClick={() => navigate("/contact")}>
              Request a Cloud Assessment
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
